import { z } from "zod";

export const HANDBOOK_SLUG_REGEX = /^[a-z0-9][a-z0-9-]{0,99}$/;
const CONTENT_MAX = 262144;

export const handbookFormSchema = z.object({
  slug: z
    .string()
    .min(1, "슬러그를 입력해주세요")
    .max(100)
    .regex(
      HANDBOOK_SLUG_REGEX,
      "슬러그는 소문자/숫자/하이픈만 가능합니다 (예: getting-started)",
    ),
  title: z.string().min(1, "제목을 입력해주세요").max(200),
  content: z
    .string()
    .min(1, "본문을 입력해주세요")
    .max(CONTENT_MAX, "본문이 256KB를 초과합니다"),
  excerpt: z.string().max(400).optional().default(""),
  tags: z.array(z.string().min(1)).max(20).default([]),
  subtitle: z.string().max(200, "부제는 200자 이내").optional().default(""),
  thumbnailUrl: z
    .union([
      z.literal(""),
      z.string().url("올바른 URL 형식이어야 합니다 (http(s)://...)").max(500),
    ])
    .optional()
    .default(""),
  parentIds: z
    .array(z.string())
    .max(10, "상위 항목은 최대 10개까지 선택 가능합니다")
    .optional()
    .default([]),
  relatedIds: z
    .array(z.string())
    .max(10, "관련 문서는 최대 10개까지 선택 가능합니다")
    .optional()
    .default([]),
  // /about/* 슬롯. null = 일반 doc.
  aboutSlot: z
    .enum(["intro", "korean-occult"])
    .nullable()
    .optional()
    .default(null),
  footnotes: z
    .array(
      z.object({
        id: z.string().min(1).max(64),
        content: z.string().max(4096, "각주는 4KB 이내"),
      }),
    )
    .max(50, "각주는 최대 50개까지")
    // 본문 마커 ↔ footnotes 1:1 매칭을 위해 id 중복 차단. 백엔드 sanitize 단에서도
    // dedup 하지만 프론트에서 즉시 피드백 — 사용자가 같은 id 를 두 row 에 적는 실수
    // (수동 편집 등) 를 막는다.
    .superRefine((footnotes, ctx) => {
      const seen = new Map<string, number>();
      footnotes.forEach((fn, index) => {
        const prev = seen.get(fn.id);
        if (prev !== undefined) {
          ctx.addIssue({
            code: "custom",
            path: [index, "id"],
            message: `중복된 각주 id 입니다 (${prev + 1}번째 각주와 같음)`,
          });
        } else {
          seen.set(fn.id, index);
        }
      });
    })
    .optional()
    .default([]),
});

export type HandbookFormValues = z.infer<typeof handbookFormSchema>;
